/**
 * Plugin Registry
 * Central registry for all service plugins
 */
import { createLogger } from '../logger.js';
import { collection, doc, getDoc, setDoc } from 'firebase/firestore';
const logger = createLogger('plugin-registry');
class PluginRegistry {
    plugins = new Map();
    activeServices = new Map();
    db = null;
    loaded = false;
    /**
     * Connect to Firestore for persistent state.
     * Call this after Firebase is initialized.
     */
    connect(firestore) {
        this.db = firestore;
    }
    /**
     * Load saved plugin states from Firestore.
     * Merges saved states over defaults.
     */
    async loadStates() {
        if (!this.db || this.loaded)
            return;
        try {
            const docRef = doc(collection(this.db, 'platformSettings'), 'pluginStates');
            const snapshot = await getDoc(docRef);
            if (snapshot.exists()) {
                const saved = snapshot.data()?.services || {};
                for (const [id, service] of Object.entries(saved)) {
                    const existing = this.activeServices.get(id);
                    if (existing) {
                        existing.isActive = service.isActive !== false;
                        existing.isEnabledForB2B = service.isEnabledForB2B !== false;
                        existing.config = { ...existing.config, ...(service.config || {}) };
                    }
                }
                logger.info('Loaded plugin states from Firestore');
            }
            this.loaded = true;
        }
        catch (err) {
            logger.warn('Failed to load plugin states from Firestore, using defaults');
            this.loaded = true;
        }
    }
    /**
     * Save plugin states to Firestore.
     */
    async saveStates() {
        if (!this.db)
            return;
        try {
            const services = {};
            for (const [id, svc] of this.activeServices) {
                services[id] = {
                    isActive: svc.isActive,
                    isEnabledForB2B: svc.isEnabledForB2B,
                };
            }
            const docRef = doc(collection(this.db, 'platformSettings'), 'pluginStates');
            await setDoc(docRef, { services, updatedAt: new Date() }, { merge: true });
        }
        catch (err) {
            logger.warn('Failed to save plugin states to Firestore');
        }
    }
    register(module) {
        const { id } = module.manifest;
        if (this.plugins.has(id)) {
            logger.warn(`Plugin ${id} already registered, overwriting`);
        }
        this.plugins.set(id, module);
        this.activeServices.set(id, {
            id,
            manifest: module.manifest,
            isActive: true,
            isEnabledForB2B: module.manifest.supportsB2B,
            config: {},
        });
        logger.info(`Plugin registered: ${id} v${module.manifest.version}`);
    }
    unregister(id) {
        this.plugins.delete(id);
        this.activeServices.delete(id);
        logger.info(`Plugin unregistered: ${id}`);
    }
    get(id) {
        return this.plugins.get(id);
    }
    getActiveService(id) {
        return this.activeServices.get(id);
    }
    getAll() {
        return Array.from(this.plugins.values());
    }
    /** Return ALL registered ActiveServices regardless of isActive state */
    getAllServices() {
        return Array.from(this.activeServices.values());
    }
    getActiveServices(filter) {
        let services = Array.from(this.activeServices.values())
            .filter(s => s.isActive);
        if (filter?.role) {
            services = services.filter(s => s.manifest.allowedRoles.includes(filter.role));
        }
        if (filter?.b2bOnly) {
            services = services.filter(s => s.isEnabledForB2B);
        }
        if (filter?.supportsB2B === false) {
            services = services.filter(s => !s.manifest.supportsB2B);
        }
        return services;
    }
    setActive(id, active) {
        const service = this.activeServices.get(id);
        if (service) {
            service.isActive = active;
            this.saveStates();
            logger.info(`Plugin ${id} ${active ? 'activated' : 'deactivated'}`);
        }
    }
    setB2BEnabled(id, enabled) {
        const service = this.activeServices.get(id);
        if (service) {
            service.isEnabledForB2B = enabled;
            this.saveStates();
            logger.info(`Plugin ${id} B2B ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    updateConfig(id, config) {
        const service = this.activeServices.get(id);
        if (service) {
            service.config = { ...service.config, ...config };
        }
    }
}
export const registry = new PluginRegistry();
export { PluginRegistry };
//# sourceMappingURL=registry.js.map